import { takeLatest, put, select } from 'redux-saga/effects';
import { getRealtimeData } from '@api/metricContainer';
import { autoRequest } from '@api/createRequestStore';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import { createFilterRule } from '@utils/filter';
import { DATE_SHORTCUTS, isAppModule } from '@utils/constants';
import update from 'immutability-helper';
import get from 'lodash/get';
import { getDefaultStore } from 'jotai';
import { getCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';

import * as actions from './actions';
import * as types from './types';
import * as selectors from './selectors';
import * as baseTypes from '../base/types';

import { applyDrilldown } from '../base/actions';
import { getThresholdData } from '../threshold/actions';
import { isThresholdDisabledSelector } from '../threshold/selectors';
import { checkIfSampledCluster } from '@components/common/SampledDataWarning';
import { handleServerError } from '@utils/errors/serverError';
import calcShortcutInterval from '@utils/calcShortcutInterval';
import { triggerDeletedFieldsError } from '@utils/errors/triggerPageError';

function* initRequest() {
  yield put(actions.init());
}

function* fetching() {
  const payload = yield select(selectors.payloadSelector);

  if (!payload || payload.length <= 1) {
    return;
  }
  const currentKpiConfig = getCurrentKpiConfig(getDefaultStore());
  if (isAppModule) {
    payload.queries.forEach((query) => {
      const [start, end] = calcShortcutInterval(DATE_SHORTCUTS.last15Mins);
      query.interval = `${start.format()}/${end.format()}`;
    });
  }
  payload.queries.forEach((query) => {
    if (!query.options) query.options = {};
    query.options.kpiConfig = currentKpiConfig ?? {};
  });
  try {
    yield put(actions.fetching());
    yield autoRequest(getRealtimeData, types.SCOPE, payload, (response) => {
      const annotations = get(response, [0, 'annotations'], []);
      checkIfSampledCluster(annotations);
      triggerDeletedFieldsError(annotations);
    });

    // fetch threshold data if feature is enabled
    const isNocDisabled = yield select(isThresholdDisabledSelector);
    if (!isNocDisabled) {
      yield put(getThresholdData());
    }
  } catch (error) {
    handleServerError(error);
  }
}

function* drilldown(action) {
  const { metric, row, navigate } = action.params;

  // get current dimesion and filter
  const dimension = yield select(selectors.dimensionSelector);
  const dimensionList = yield select(dimensionsListSelector);
  const filter = yield select(selectors.filterSelector);
  const dimensionObj = dimensionList.find((d) => d.groupBy === dimension);

  // create drill down rule
  const drilldownRule = createFilterRule(
    dimensionObj,
    get(row, 'name[0]', ''),
    get(row, 'value[0]', ''),
  );

  // add drilldown rule to current filter rules
  const combinedRules = update(filter.rule, {
    $push: [[drilldownRule]],
  });

  // base saga will trigger the apply action
  yield put(applyDrilldown({ metric, rules: combinedRules, navigate }));
}

function* watchInitRequest() {
  yield takeLatest([types.INIT], initRequest);
}

function* watchFetching() {
  yield takeLatest(
    [
      types.FETCH_DATA,
      baseTypes.SET_DIMENSION,
      baseTypes.SET_TRENDS_METRICS,
      baseTypes.APPLY_TRENDS_SELECTED_NOC_FILTERS,
    ],
    fetching,
  );
}

function* watchDrilldown() {
  yield takeLatest([types.DRILLDOWN], drilldown);
}

export default [watchInitRequest, watchFetching, watchDrilldown];
